"use client";

import { useEffect, useId, useRef, useState } from "react";
import { auditoriaPage } from "@/content/auditoria";
import { thousands } from "@/content/research-data";
import { worldBus } from "@/lib/world-bus";
import { REGIONS, SKY_GROUPS } from "@/world/data/families";
import styles from "./audit.module.css";

// El cielo de modelos explorable (guía 7.4 y 8.6): órbita libre con arrastre o táctil,
// filtros por región y por familia, información al pasar o tocar y "Encontrar el
// nuestro", que lleva la cámara en un vuelo largo hasta el punto solitario.
//
// La escena vive en el mundo (src/world/scene/sky.ts); acá solo se escriben los
// controles en worldBus.audit y se leen las posiciones en pantalla que el mundo publica.

const t = auditoriaPage.explorer;
const PITCH = [-0.55, 0.95] as const;
const ZOOM = [0.32, 1.6] as const;

type Tip = { x: number; y: number; name: string; count: number } | null;

export function SkyExplorer() {
  const surfaceRef = useRef<HTMLDivElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const oursRef = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<Tip>(null);
  const [found, setFound] = useState(false);
  const [family, setFamily] = useState(-1);
  const [regions, setRegions] = useState<[number, number, number]>([1, 1, 1]);
  const selectId = useId();
  const descId = useId();

  // Filtros → escena.
  useEffect(() => {
    const a = worldBus.audit;
    a.familyMask = SKY_GROUPS.map((_, i) => (family < 0 || i === family ? 1 : 0));
    a.focus = family;
    a.regionMask = regions;
  }, [family, regions]);

  useEffect(() => {
    worldBus.audit.findOurs = found;
  }, [found]);

  // Al salir de la página, el cielo vuelve a su estado general.
  useEffect(
    () => () => {
      const a = worldBus.audit;
      a.findOurs = false;
      a.familyMask = [];
      a.focus = -1;
      a.regionMask = [1, 1, 1];
    },
    [],
  );

  // Etiqueta del punto nuestro, pegada a su posición en pantalla.
  useEffect(() => {
    let raf = 0;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const el = oursRef.current;
      const o = worldBus.oursScreen;
      const surface = surfaceRef.current;
      if (!el || !surface) return;
      const box = surface.getBoundingClientRect();
      const show = found && o !== null;
      el.style.opacity = show ? "1" : "0";
      if (o) el.style.transform = `translate(${o.x - box.left}px, ${o.y - box.top}px)`;
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [found]);

  useEffect(() => {
    const el = surfaceRef.current;
    if (!el) return;
    let dragging = false;
    let moved = 0;
    let lx = 0;
    let ly = 0;
    const a = worldBus.audit;

    const pick = (cx: number, cy: number): Tip => {
      let best: Tip = null;
      let bestD = Infinity;
      worldBus.familyScreen.forEach((f, i) => {
        if (!f?.visible) return;
        const d = Math.hypot(f.x - cx, f.y - cy);
        const reach = Math.max(36, Math.min(f.r, 260));
        if (d < reach && d < bestD) {
          bestD = d;
          best = { x: cx, y: cy, name: SKY_GROUPS[i].name, count: SKY_GROUPS[i].count };
        }
      });
      return best;
    };

    const down = (e: PointerEvent) => {
      dragging = true;
      moved = 0;
      lx = e.clientX;
      ly = e.clientY;
      el.setPointerCapture(e.pointerId);
    };
    const move = (e: PointerEvent) => {
      if (dragging) {
        const dx = e.clientX - lx;
        const dy = e.clientY - ly;
        lx = e.clientX;
        ly = e.clientY;
        moved += Math.abs(dx) + Math.abs(dy);
        a.yaw -= dx * 0.0042;
        // En táctil el arrastre vertical queda para el scroll de la página.
        if (e.pointerType === "mouse") a.pitch = Math.min(PITCH[1], Math.max(PITCH[0], a.pitch + dy * 0.003));
        if (found) setFound(false);
        setTip(null);
        return;
      }
      if (e.pointerType === "mouse") {
        const box = el.getBoundingClientRect();
        const p = pick(e.clientX, e.clientY);
        setTip(p ? { ...p, x: p.x - box.left, y: p.y - box.top } : null);
      }
    };
    const up = (e: PointerEvent) => {
      dragging = false;
      if (moved < 8) {
        // Toque o clic sin arrastre: muestra la familia que hay ahí.
        const box = el.getBoundingClientRect();
        const p = pick(e.clientX, e.clientY);
        setTip(p ? { ...p, x: p.x - box.left, y: p.y - box.top } : null);
      }
    };
    const leave = () => {
      if (!dragging) setTip(null);
    };
    const key = (e: KeyboardEvent) => {
      const step = 0.08;
      if (e.key === "ArrowLeft") a.yaw += step;
      else if (e.key === "ArrowRight") a.yaw -= step;
      else if (e.key === "ArrowUp") a.pitch = Math.min(PITCH[1], a.pitch + step * 0.6);
      else if (e.key === "ArrowDown") a.pitch = Math.max(PITCH[0], a.pitch - step * 0.6);
      else if (e.key === "+" || e.key === "=") a.zoom = Math.max(ZOOM[0], a.zoom * 0.85);
      else if (e.key === "-") a.zoom = Math.min(ZOOM[1], a.zoom / 0.85);
      else return;
      e.preventDefault();
    };
    el.addEventListener("pointerdown", down);
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", up);
    el.addEventListener("pointerleave", leave);
    el.addEventListener("keydown", key);
    return () => {
      el.removeEventListener("pointerdown", down);
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerup", up);
      el.removeEventListener("pointercancel", up);
      el.removeEventListener("pointerleave", leave);
      el.removeEventListener("keydown", key);
    };
  }, [found]);

  const zoom = (k: number) => {
    const a = worldBus.audit;
    a.zoom = Math.min(ZOOM[1], Math.max(ZOOM[0], a.zoom * k));
  };

  return (
    <section className={styles.explorer} aria-label={t.label}>
      <p id={descId} className="sr-only">
        {t.description}
      </p>
      <div
        ref={surfaceRef}
        className={styles.surface}
        tabIndex={0}
        role="application"
        aria-roledescription="escena 3D"
        aria-label={`${t.label}. ${t.hint} Con el teclado: flechas para girar, más y menos para acercar.`}
        aria-describedby={descId}
      >
        {tip && (
          <div ref={tipRef} className={styles.tip} style={{ transform: `translate(${tip.x}px, ${tip.y}px)` }} role="status">
            <span className={styles.tipName}>{tip.name}</span>
            <span className={styles.tipCount}>
              {thousands(tip.count)} {t.countNote}
            </span>
          </div>
        )}
        <div ref={oursRef} className={styles.ours} aria-hidden={!found}>
          <span>{t.ours}</span>
        </div>
      </div>

      <div className={styles.controls}>
        <p className={styles.hint}>{t.hint}</p>
        <div className={styles.row}>
          <button type="button" className={styles.find} aria-pressed={found} onClick={() => setFound((v) => !v)}>
            {found ? t.back : t.find}
          </button>
          <div className={styles.zoom}>
            <button type="button" onClick={() => zoom(0.8)} aria-label={t.zoomIn}>
              +
            </button>
            <button type="button" onClick={() => zoom(1.25)} aria-label={t.zoomOut}>
              −
            </button>
          </div>
        </div>
        <div className={styles.row}>
          <fieldset className={styles.regions}>
            <legend className={styles.legend}>{t.regions}</legend>
            {REGIONS.map((r, i) => (
              <button
                key={r.id}
                type="button"
                className={styles.chip}
                aria-pressed={regions[i] === 1}
                onClick={() =>
                  setRegions((prev) => {
                    const next = [...prev] as [number, number, number];
                    next[i] = next[i] ? 0 : 1;
                    // Nunca las tres apagadas: el cielo vacío no explica nada.
                    return next.some(Boolean) ? next : prev;
                  })
                }
              >
                {r.name} · {thousands(r.count)}
              </button>
            ))}
          </fieldset>
          <div className={styles.family}>
            <label htmlFor={selectId} className={styles.legend}>
              {t.families}
            </label>
            <select id={selectId} className={styles.select} value={family} onChange={(e) => setFamily(Number(e.target.value))}>
              <option value={-1}>{t.all}</option>
              {SKY_GROUPS.map((g, i) => (
                <option key={g.id} value={i}>
                  {g.name} · {thousands(g.count)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </section>
  );
}
